import {Injectable} from '@angular/core';
import {AbstractControl, FormGroup, ValidatorFn} from '@angular/forms';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class CustomValidationService {

  passwordPatternValidator(): ValidatorFn {

    // Sequences
    const passwordLetters = [];
    return (control: AbstractControl): { [key: string]: any } => {
      if (control.value) {
        for (let i = 0; i < control.value.length; i++) {
          passwordLetters.push(control.value[i].charCodeAt(0));
          if (i > 1) {
            if ((passwordLetters[i - 2] == passwordLetters[i] - 2 && passwordLetters[i - 1] && passwordLetters[i] - 1) ||
              (passwordLetters[i - 2] == passwordLetters[i] + 2 && passwordLetters[i - 1] && passwordLetters[i] + 1)) {
              return {sequenceInPassword: true};
            }
          }
        }
      }

      // Character repetition
      const counts = [];
      passwordLetters.forEach(function callback(x) {
        counts[x] = (counts[x] || 0) + 1;
      });
      for (const letterCount of counts) {
        if (letterCount > 2) {
          return {repetitionInPassword: true};
        }
      }

      // Spaces
      const regex = new RegExp(/^\S*$/);
      const valid = regex.test(control.value);
      if (valid) {
        return null;
      } else {
        return {spacesInPassword: true};
      }
    };
  }

  MatchPassword(password: string, confirmPassword: string): any {
    return (formGroup: FormGroup) => {
      const passwordControl = formGroup.controls[password];
      const confirmPasswordControl = formGroup.controls[confirmPassword];

      if (!passwordControl || !confirmPasswordControl) {
        return null;
      }

      if (confirmPasswordControl.errors && !confirmPasswordControl.errors.passwordMismatch) {
        return null;
      }

      if (passwordControl.value !== confirmPasswordControl.value) {
        confirmPasswordControl.setErrors({passwordMismatch: true});
      } else {
        confirmPasswordControl.setErrors(null);
      }
    };
  }

  MatchNip(nip: string, confirmNip: string): any {
    return (formGroup: FormGroup) => {
      const nipControl = formGroup.controls[nip];
      const confirmNipControl = formGroup.controls[confirmNip];

      if (!nipControl || !confirmNipControl) {
        return null;
      }

      if (confirmNipControl.errors && !confirmNipControl.errors.nipMismatch) {
        return null;
      }

      if (nipControl.value !== confirmNipControl.value) {
        confirmNipControl.setErrors({nipMismatch: true});
      } else {
        confirmNipControl.setErrors(null);
      }
    };
  }

  MatchEmail(email: string, confirmEmail: string): any {
    return (formGroup: FormGroup) => {
      const emailControl = formGroup.controls[email];
      const confirmEmailControl = formGroup.controls[confirmEmail];

      if (!emailControl || !confirmEmailControl) {
        return null;
      }

      if (confirmEmailControl.errors && !confirmEmailControl.errors.emailMismatch) {
        return null;
      }

      if (emailControl.value !== confirmEmailControl.value) {
        confirmEmailControl.setErrors({emailMismatch: true});
      } else {
        confirmEmailControl.setErrors(null);
      }
    };
  }

  numberPatternValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const regex = new RegExp('^(0|[0-9]*)$');
      const valid = regex.test(control.value);
      return valid ? null : {invalidNumber: true};
    };
  }

  notNullValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return {notNullError: true};
      }
    };
  }

  AddressNumberPatternValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const regex = new RegExp('(^[A-Za-z0-9\\-]+)$');
      const valid = regex.test(control.value);
      return valid ? null : {addressNumberError: true};
    };
  }

  AddressStreetPatternValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const regex = new RegExp('(^[A-Za-z0-9#\\-\\s]+)$');
      const valid = regex.test(control.value);
      return valid ? null : {addressStreetError: true};
    };
  }

  positiveNumberPatternValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return control.value > 0 ? null : {negativeNumber: true};
    };
  }

  barcodeMinimumAmountValidator(barcodeMin: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return control.value >= barcodeMin ? null : {barcodeMinimumAmount: true, barcodeMin};
    };
  }
  barcodeMaximumAmountValidator(barcodeMax: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return control.value <= barcodeMax ? null : {barcodeMaximumAmount: true, barcodeMax};
    };
  }

  maximumAmountPatternValidator(maxValue: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return control.value < maxValue ? null : {maximumAmount: true};
    };
  }

  textPatternValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const regex = new RegExp('(^[a-zA-ZñÑãáéíóúüçÃÁÉÍÓÚÜÇ ]+[a-zA-ZñÑãáéíóúüçÃÁÉÍÓÚÜÇ ]{0,20}$)');
      const valid = regex.test(control.value);
      return valid ? null : {invalidText: true};
    };
  }

  emailPatternValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const regex = new RegExp('(^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,30}$)');
      const valid = regex.test(control.value);
      return valid ? null : {email: true};
    };
  }

  isAdultValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const date = moment([control.value.year, control.value.month - 1, control.value.day]).add(21, 'years');
      return date.isBefore(moment(), 'day') ? null : {invalidIsAdult: true};
    };
  }

  isReferredAdultValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const date = moment([control.value.year, control.value.month - 1, control.value.day]).add(18, 'years');
      return date.isBefore(moment(), 'day') ? null : {invalidIsReferredAdult: true};
    };
  }

}
